import { valueLabel } from "../shared/value-copy";
import { useMemo, type ReactNode } from "react";
import {
  Alert,
  Button,
  Card,
  Field,
  Grid,
  Inline,
  KpiCard,
  Panel,
  SelectField,
  Spinner,
  Stack,
  StatusBadge,
  formatMoneyMinor
} from "@leylion/design-system";
import type {
  AnalyticsBreakdown,
  AnalyticsKpi,
  AnalyticsSeriesPoint,
  AnalyticsTable,
  ReportScope
} from "../api/reports-types";

export type ReportsLanguage = "ar" | "en";

export function money(
  minor: number | null | undefined,
  language: ReportsLanguage,
  currency = "AED"
): string {
  return formatMoneyMinor(
    minor ?? 0,
    currency,
    language === "ar" ? "ar-AE" : "en-AE"
  );
}

export function quantity(
  value: number | null | undefined,
  language: ReportsLanguage,
  maximumFractionDigits = 2
): string {
  return new Intl.NumberFormat(language === "ar" ? "ar-AE" : "en-AE", {
    maximumFractionDigits
  }).format(value ?? 0);
}

export function formatMetric(
  value: number,
  format: string,
  language: ReportsLanguage,
  currency = "AED"
): string {
  if (format === "money") return money(value, language, currency);
  if (format === "percent") return `${quantity(value, language)}%`;
  if (format === "minutes") {
    return `${quantity(value, language)} ${language === "ar" ? "د" : "min"}`;
  }
  return quantity(value, language);
}

export function dateLabel(
  value: string | null | undefined,
  language: ReportsLanguage
): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(language === "ar" ? "ar-AE" : "en-AE", {
    year: "numeric",
    month: "short",
    day: "numeric"
  }).format(new Date(value));
}

export function dateTimeLabel(
  value: string | null | undefined,
  language: ReportsLanguage
): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(language === "ar" ? "ar-AE" : "en-AE", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

export function statusTone(
  value: string | null | undefined
): "success" | "warning" | "danger" | "info" | "neutral" {
  const normalized = (value ?? "").toLowerCase();
  if (
    normalized.includes("success")
    || normalized.includes("healthy")
    || normalized.includes("excellent")
    || normalized.includes("posted")
    || normalized.includes("completed")
    || normalized.includes("active")
    || normalized.includes("balanced")
    || normalized.includes("open")
  ) {
    return "success";
  }
  if (
    normalized.includes("critical")
    || normalized.includes("danger")
    || normalized.includes("loss")
    || normalized.includes("failed")
    || normalized.includes("closed")
    || normalized.includes("out")
  ) {
    return "danger";
  }
  if (
    normalized.includes("warning")
    || normalized.includes("review")
    || normalized.includes("low")
    || normalized.includes("draft")
    || normalized.includes("soft")
    || normalized.includes("stagnant")
  ) {
    return "warning";
  }
  if (
    normalized.includes("info")
    || normalized.includes("running")
    || normalized.includes("queued")
    || normalized.includes("scheduled")
  ) {
    return "info";
  }
  return "neutral";
}

export function defaultReportScope(currency: string): ReportScope {
  const now = new Date();
  const from = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    1
  ));
  const to = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth() + 1,
    1
  ));
  const compareTo = from;
  const compareFrom = new Date(
    compareTo.getTime() - (to.getTime() - from.getTime())
  );
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    branchIds: [],
    currency,
    compareFrom: compareFrom.toISOString(),
    compareTo: compareTo.toISOString()
  };
}

function toDateInput(value: string): string {
  return value.slice(0, 10);
}

function fromDateInput(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

export function ScopeBar({
  language,
  scope,
  onChange,
  onApply,
  busy,
  allowEnterprise
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  onChange: (scope: ReportScope) => void;
  onApply: () => void;
  busy?: boolean;
  allowEnterprise?: boolean;
}) {
  const ar = language === "ar";
  return (
    <Panel className="reports-scope-panel">
      <div className="reports-scope">
        <Field
          label={ar ? "من" : "From"}
          type="date"
          value={toDateInput(scope.from)}
          onChange={(event) => {
            const from = fromDateInput(event.target.value);
            const currentLength =
              new Date(scope.to).getTime() - new Date(scope.from).getTime();
            const nextTo = new Date(
              new Date(from).getTime() + currentLength
            ).toISOString();
            const compareTo = from;
            const compareFrom = new Date(
              new Date(compareTo).getTime() - currentLength
            ).toISOString();
            onChange({
              ...scope,
              from,
              to: nextTo,
              compareFrom,
              compareTo
            });
          }}
        />
        <Field
          label={ar ? "إلى" : "To"}
          type="date"
          value={toDateInput(scope.to)}
          onChange={(event) => {
            const next = new Date(
              `${event.target.value}T00:00:00.000Z`
            );
            next.setUTCDate(next.getUTCDate() + 1);
            const to = next.toISOString();
            const length =
              new Date(to).getTime() - new Date(scope.from).getTime();
            onChange({
              ...scope,
              to,
              compareFrom: new Date(
                new Date(scope.from).getTime() - length
              ).toISOString(),
              compareTo: scope.from
            });
          }}
        />
        <SelectField
          label={ar ? "العملة" : "Currency"}
          value={scope.currency}
          onChange={(event) => onChange({
            ...scope,
            currency: event.target.value
          })}
        >
          {!["AED", "USD", "SAR", "JOD"].includes(scope.currency) ? <option value={scope.currency}>{scope.currency}</option> : null}
          <option value="AED">AED</option>
          <option value="USD">USD</option>
          <option value="SAR">SAR</option>
          <option value="JOD">JOD</option>
        </SelectField>
        <div className="reports-scope__context">
          <span>{ar ? "النطاق" : "Scope"}</span>
          <strong>
            {allowEnterprise
              ? ar ? "الفروع المحددة / جميع الفروع" : "Selected / all branches"
              : ar ? "الفرع الحالي" : "Current branch"}
          </strong>
        </div>
        <Button
          data-testid="reports-apply-scope"
          icon="filter"
          disabled={busy}
          onClick={onApply}
        >
          {busy
            ? ar ? "جارٍ التحديث" : "Refreshing"
            : ar ? "تطبيق" : "Apply"}
        </Button>
      </div>
    </Panel>
  );
}

export function LoadingReport({
  language,
  message
}: {
  language: ReportsLanguage;
  message?: string;
}) {
  return (
    <Card>
      <Inline>
        <Spinner label={language === "ar" ? "تحميل" : "Loading"} />
        <span>
          {message
            ?? (language === "ar"
              ? "جارٍ احتساب مؤشرات التقرير…"
              : "Calculating report metrics…")}
        </span>
      </Inline>
    </Card>
  );
}

export function ReportError({
  language,
  error
}: {
  language: ReportsLanguage;
  error: string;
}) {
  return (
    <Alert
      tone="danger"
      title={language === "ar" ? "تعذر تحميل التقرير" : "Report failed"}
    >
      {error}
    </Alert>
  );
}

export function comparisonLabel(value: number, previous: number | null, change: number | null, language: ReportsLanguage): string {
  const ar = language === "ar";
  if (previous == null) return ar ? "لا توجد مقارنة" : "No comparison";
  if (previous === 0) return value === 0 ? (ar ? "لا يوجد نشاط" : "No activity") : (ar ? "من أساس صفري" : "From zero base");
  if (value === previous) return ar ? "دون تغير" : "Unchanged";
  if (change == null || !Number.isFinite(change)) return ar ? "لا توجد نسبة مقارنة" : "No comparison rate";
  if (Math.abs(change) < 0.01) return `${value > previous ? "+" : "−"}<0.01%`;
  return `${change > 0 ? "+" : ""}${quantity(change, language)}%`;
}

export function KpiGrid({
  language,
  kpis,
  currency = "AED"
}: {
  language: ReportsLanguage;
  kpis: AnalyticsKpi[];
  currency?: string;
}) {
  return (
    <Grid columns={4}>
      {kpis.map((kpi) => (
        <KpiCard
          key={kpi.key}
          label={language === "ar" ? kpi.labelAr : kpi.labelEn}
          value={formatMetric(kpi.value, kpi.format, language, currency)}
          trend={comparisonLabel(kpi.value, kpi.comparisonValue, kpi.changePercent, language)}
          trendTone={kpi.comparisonValue == null || kpi.comparisonValue === 0 || kpi.value === kpi.comparisonValue
            ? "neutral" : kpi.tone === "success" ? "success" : kpi.tone === "warning" ? "warning" : kpi.tone === "danger" ? "danger" : "neutral"}
          hint={
            kpi.comparisonValue == null
              ? undefined
              : language === "ar"
                ? "مقارنة بالفترة السابقة"
                : "vs comparison period"
          }
        />
      ))}
    </Grid>
  );
}

export function TrendChart({
  language,
  title,
  series,
  valueFormat = "money",
  currency = "AED"
}: {
  language: ReportsLanguage;
  title: string;
  series: AnalyticsSeriesPoint[];
  valueFormat?: string;
  currency?: string;
}) {
  const dimensions = {
    width: 900,
    height: 280,
    left: 160,
    right: 24,
    top: 24,
    bottom: 46
  };

  const values = series.flatMap((point) => [
    point.value,
    point.comparisonValue ?? point.value
  ]).filter(Number.isFinite);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const plotWidth =
    dimensions.width - dimensions.left - dimensions.right;
  const plotHeight =
    dimensions.height - dimensions.top - dimensions.bottom;

  const coordinate = (value: number, index: number) => ({
    x: dimensions.left + (series.length <= 1 ? plotWidth / 2 : index / (series.length - 1) * plotWidth),
    y: dimensions.top + (1 - (value - min) / range) * plotHeight
  });
  const toPath = (selector: (point: AnalyticsSeriesPoint) => number | null) => {
    let penDown = false;
    return series.map((point, index) => {
      const value = selector(point);
      if (value == null || !Number.isFinite(value)) { penDown = false; return ""; }
      const { x, y } = coordinate(value, index);
      const command = `${penDown ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
      penDown = true;
      return command;
    }).filter(Boolean).join(" ");
  };

  const currentPath = toPath((point) => point.value);
  const comparisonPath = toPath((point) => point.comparisonValue);
  const firstLabel = series[0]?.label ?? "";
  const middleLabel = series[Math.floor(series.length / 2)]?.label ?? "";
  const lastLabel = series.at(-1)?.label ?? "";

  return (
    <Panel
      title={title}
      description={
        language === "ar"
          ? "الخط المتصل للفترة الحالية والمتقطع لفترة المقارنة."
          : "Solid line is current period; dashed line is comparison."
      }
    >
      {series.length === 0 ? (
        <p className="reports-muted">
          {language === "ar" ? "لا توجد بيانات." : "No data."}
        </p>
      ) : (
        <div className="reports-chart-wrap">
          <svg
            style={{ direction: "ltr" }}
            className="reports-line-chart"
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            role="img"
            aria-label={String(title)}
          >
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
              const y = dimensions.top + ratio * plotHeight;
              const value = max - ratio * range;
              return (
                <g key={ratio}>
                  <line
                    x1={dimensions.left}
                    x2={dimensions.width - dimensions.right}
                    y1={y}
                    y2={y}
                    className="reports-chart-grid"
                  />
                  <text
                    x={dimensions.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    className="reports-chart-label"
                  >
                    {valueFormat === "money"
                      ? money(value, language, currency)
                      : quantity(value, language)}
                  </text>
                </g>
              );
            })}
            {comparisonPath ? (
              <path
                d={comparisonPath}
                className="reports-chart-line reports-chart-line--comparison"
              />
            ) : null}
            <path
              d={currentPath}
              className="reports-chart-line reports-chart-line--current"
            />
            {series.map((point, index) => <g key={index}>
              {point.comparisonValue != null && Number.isFinite(point.comparisonValue) ? <circle {...{ cx: coordinate(point.comparisonValue, index).x, cy: coordinate(point.comparisonValue, index).y }} r={3} className="reports-chart-point--comparison" /> : null}
              {Number.isFinite(point.value) ? <circle {...{ cx: coordinate(point.value, index).x, cy: coordinate(point.value, index).y }} r={3} className="reports-chart-point--current" /> : null}
            </g>)}
            <text
              x={dimensions.left}
              y={dimensions.height - 14}
              textAnchor="start"
              className="reports-chart-label"
            >
              {firstLabel}
            </text>
            <text
              x={dimensions.left + plotWidth / 2}
              y={dimensions.height - 14}
              textAnchor="middle"
              className="reports-chart-label"
            >
              {middleLabel}
            </text>
            <text
              x={dimensions.width - dimensions.right}
              y={dimensions.height - 14}
              textAnchor="end"
              className="reports-chart-label"
            >
              {lastLabel}
            </text>
          </svg>
        </div>
      )}
    </Panel>
  );
}

export function BreakdownBars({
  language,
  title,
  rows,
  valueFormat = "money",
  currency = "AED",
  maximum = 8
}: {
  language: ReportsLanguage;
  title: string;
  rows: AnalyticsBreakdown[];
  valueFormat?: "money" | "number" | "percent";
  currency?: string;
  maximum?: number;
}) {
  const visible = rows.slice(0, maximum);

  return (
    <Panel title={title}>
      <div className="reports-breakdowns">
        {visible.length === 0 ? (
          <span className="reports-muted">
            {language === "ar" ? "لا توجد بيانات." : "No data."}
          </span>
        ) : visible.map((row) => (
          <div className="reports-breakdown" key={row.key}>
            <div className="reports-breakdown__header">
              <span>{language === "ar" ? row.labelAr : row.labelEn}</span>
              <strong>
                {valueFormat === "money"
                  ? money(row.value, language, currency)
                  : valueFormat === "percent"
                    ? `${quantity(row.value, language)}%`
                    : quantity(row.value, language)}
              </strong>
            </div>
            <div className="reports-breakdown__track">
              <span
                style={{
                  inlineSize: `${Number.isFinite(row.sharePercent) ? Math.min(100, Math.max(0, row.sharePercent)) : 0}%`
                }}
              />
            </div>
            <div className="reports-breakdown__meta">
              <small>{quantity(row.sharePercent, language)}%</small>
              {row.changePercent != null ? (
                <StatusBadge
                  tone={row.comparisonValue == null || row.comparisonValue === 0 || row.value === row.comparisonValue ? "neutral" : row.changePercent > 0 ? "success" : "warning"}
                >
                  {comparisonLabel(row.value, row.comparisonValue, row.changePercent, language)}
                </StatusBadge>
              ) : row.status ? (
                <StatusBadge tone={statusTone(row.status)}>
                  {row.status}
                </StatusBadge>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

const moneyKeys = [
  "minor",
  "sales",
  "revenue",
  "profit",
  "cost",
  "value",
  "spend",
  "cash",
  "credit",
  "debit",
  "balance",
  "subtotal",
  "discount",
  "refund",
  "total",
  "payable",
  "expense"
];

const reportColumnLabels: Record<string, [string, string]> = {
  date: ["التاريخ", "Date"], branch: ["الفرع", "Branch"], serviceType: ["نوع الخدمة", "Service type"],
  orders: ["عدد الطلبات", "Orders"], grossMinor: ["الإجمالي", "Gross amount"],
  discountMinor: ["الخصم", "Discount"], refundMinor: ["المرتجعات", "Refunds"], netMinor: ["الصافي", "Net amount"],
  paymentMethod: ["طريقة الدفع", "Payment method"], currency: ["العملة", "Currency"],
  status: ["الحالة", "Status"], totalMinor: ["الإجمالي", "Total"], taxMinor: ["الضريبة", "Tax"],
  netSalesMinor: ["صافي المبيعات", "Net sales"], revenueMinor: ["الإيراد", "Revenue"],
  profitMinor: ["الربح", "Profit"], costMinor: ["التكلفة", "Cost"], marginPercent: ["هامش الربح %", "Margin %"],
  product: ["الصنف", "Product"], quantity: ["الكمية", "Quantity"], customer: ["العميل", "Customer"],
  ordersCount: ["عدد الطلبات", "Order count"], name: ["الاسم", "Name"]
};
function reportColumnLabel(column: string, language: ReportsLanguage): string {
  return reportColumnLabels[column]?.[language === "ar" ? 0 : 1]
    ?? column.replace(/Minor$/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

function formatCell(
  key: string,
  value: unknown,
  language: ReportsLanguage,
  currency: string
): ReactNode {
  if (value == null) return "—";
  if (typeof value === "boolean") {
    return (
      <StatusBadge tone={value ? "success" : "warning"}>
        {value
          ? language === "ar" ? "نعم" : "Yes"
          : language === "ar" ? "لا" : "No"}
      </StatusBadge>
    );
  }
  if (
    typeof value === "number"
    && moneyKeys.some((token) => key.toLowerCase().includes(token))
  ) {
    return money(value, language, currency);
  }
  if (
    typeof value === "number"
    && key.toLowerCase().includes("percent")
  ) {
    return `${quantity(value, language)}%`;
  }
  if (
    typeof value === "string"
    && (key.toLowerCase().includes("date")
      || key.toLowerCase().endsWith("at"))
  ) {
    return dateTimeLabel(value, language);
  }
  if (
    key.toLowerCase() === "status"
    || key.toLowerCase() === "score"
  ) {
    return (
      <StatusBadge tone={statusTone(String(value))}>
        {valueLabel(String(value), language)}
      </StatusBadge>
    );
  }
  if (typeof value === "number") return quantity(value, language);
  return ["serviceType", "paymentMethod"].includes(key) ? valueLabel(String(value), language) : String(value);
}

export function DynamicReportTable({
  language,
  title,
  table,
  currency = "AED",
  limit = 200
}: {
  language: ReportsLanguage;
  title: string;
  table: AnalyticsTable;
  currency?: string;
  limit?: number;
}) {
  const visibleRows = table.rows.slice(0, limit);
  return (
    <Panel
      title={title}
      description={
        table.rows.length > limit
          ? language === "ar"
            ? `يتم عرض أول ${limit} صفًا من ${table.rows.length}.`
            : `Showing first ${limit} of ${table.rows.length} rows.`
          : undefined
      }
    >
      <div className="reports-dynamic-table-wrap">
        <table className="reports-dynamic-table">
          <thead>
            <tr>
              {table.columns.map((column) => (
                <th key={column}>{reportColumnLabel(column, language)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(1, table.columns.length)}>
                  {language === "ar" ? "لا توجد بيانات." : "No data."}
                </td>
              </tr>
            ) : visibleRows.map((row, index) => (
              <tr key={`${index}-${String(row[table.columns[0]])}`}>
                {table.columns.map((column) => (
                  <td key={column}>
                    {formatCell(
                      column,
                      row[column],
                      language,
                      currency
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {Object.keys(table.totals).length ? (
            <tfoot>
              <tr>
                {table.columns.map((column, index) => (
                  <td key={column}>
                    {index === 0
                      ? language === "ar" ? "الإجمالي" : "Total"
                      : formatCell(
                          column,
                          table.totals[column],
                          language,
                          currency
                        )}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </Panel>
  );
}

export function TwoColumn({
  children
}: {
  children: ReactNode;
}) {
  return <div className="reports-two-column">{children}</div>;
}

export function ThreeColumn({
  children
}: {
  children: ReactNode;
}) {
  return <div className="reports-three-column">{children}</div>;
}

export function SectionHeading({
  title,
  description,
  actions
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="reports-section-heading">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <Inline>{actions}</Inline> : null}
    </div>
  );
}

export function useScopeKey(scope: ReportScope): string {
  return useMemo(() => JSON.stringify(scope), [scope]);
}
